import { useMemo } from 'react'
import dayjs from 'dayjs'
import { IntervalDataEntry, IntervalDisplayParam } from '@/hooks'
import { getEntryId } from '../utils/getEntryId'

export type DataCompleteness = {
    metersWithData: number
    totalMeters: number
}

type DataCompletenessMap = Map<string, DataCompleteness>

type UseDataCompletenessParams = {
    data: IntervalDataEntry[]
    totalMeters: number
    dataGrouping: 'aggregate' | 'compare'
    getValue: (entry: IntervalDataEntry) => number | undefined
    display: IntervalDisplayParam
}

export const useDataCompleteness = ({
    data,
    totalMeters,
    dataGrouping,
    getValue,
    display,
}: UseDataCompletenessParams): DataCompletenessMap => {
    const dataCompletenessMap = useMemo(() => {
        const completenessMap = new Map<string, DataCompleteness>()

        // Group entries by Unix timestamp string (to match chart's xKey format)
        const entriesByTimestamp = new Map<string, IntervalDataEntry[]>()
        for (const entry of data) {
            if (!entry.timestampLocal) continue
            // Convert to Unix timestamp string to match chart's xKey="timestamp" format
            const timestamp = dayjs(entry.timestampLocal).valueOf().toString()
            if (!entriesByTimestamp.has(timestamp)) {
                entriesByTimestamp.set(timestamp, [])
            }
            entriesByTimestamp.get(timestamp)?.push(entry)
        }

        // Calculate completeness for each timestamp
        for (const [
            timestampKey,
            matchingEntries,
        ] of entriesByTimestamp.entries()) {
            let completeness: DataCompleteness
            if (dataGrouping === 'aggregate') {
                // Get reportedMeters from the first matching entry (all entries for same timestamp should have same reportedMeters)
                const reportedMeters = matchingEntries.find(
                    (entry) => entry.reportedMeters !== undefined
                )?.reportedMeters

                completeness = {
                    metersWithData: reportedMeters || 0,
                    totalMeters,
                }
            } else {
                // In compare mode, count unique meterIds/subMeterIds with data
                const metersWithDataIds = new Set(
                    matchingEntries
                        .filter((entry) => getValue(entry) !== undefined)
                        .map((entry) => getEntryId(entry, display))
                        .filter((id): id is number => id !== undefined)
                )

                completeness = {
                    metersWithData: metersWithDataIds.size,
                    totalMeters,
                }
            }

            // Index by Unix timestamp string to match tooltip lookup
            completenessMap.set(timestampKey, completeness)
        }

        return completenessMap
    }, [dataGrouping, data, totalMeters, getValue, display])

    return dataCompletenessMap
}
